import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  activateSolutionsCategory,
  activateSolutionsSubcategory,
  createSolutionsCategory,
  createSolutionsSubcategory,
  editSolutionsCategory,
  editSolutionsSubcategory,
  removeSolutionsCategory,
  removeSolutionsSubcategory,
  solutionsAdminQuery,
  solutionsAdminSubcategoriesQuery,
  INHERIT_ACCESS,
  type AccessOption,
  type DocumentAccess,
  type SolutionsAdminCategory,
  type SolutionsAdminSubcategory,
  type SubcategoryForm,
} from '../api/easeedeskSolutions'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { DocumentAccessPicker } from './DocumentAccessPicker'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck } from '../ui/SettingsParts'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'

/**
 * Biblioteca furnizorului - `solutions/categories`, macheta
 * `solutions/solutions_categories.php` cu `solutions_subcategories.php` si
 * `subcategoryList.php`, scriptul `public/js/solutions.js`.
 *
 * Tabelul categoriilor (Category, Solutions, Activate, Actions), "New
 * category" cu numele si rolurile care o vad (cerinta 46), randul desfacut cu
 * solutiile ei - cate una cu cele patru descrieri de nivel si prioritatea -
 * si "New solution" / Edit / Delete pe fiecare. Stergerile intreaba, ca
 * `swal`-ul vechi. Dupa scriere se recer listele, nu se reincarca pagina.
 *
 * "Master Edit" de pe IT Solutions deschide ecranul asta.
 */

const PRIORITIES: [number, string][] = [
  [1, 'Obligatory'],
  [2, 'Recommended'],
  [3, 'Optional'],
]

const EMPTY_SUB: SubcategoryForm = { subcategory_name: '', priority: 0, level_0: '', level_1: '', level_2: '', level_3: '' }

export function SolutionsCategoriesPage() {
  const page = useQuery(solutionsAdminQuery)
  const client = useQueryClient()
  const [open, setOpen] = useState(0)
  const [editing, setEditing] = useState<'new' | SolutionsAdminCategory | null>(null)

  if (isForbidden(page.error)) return <Forbidden />
  if (page.isError) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />
  const data = page.data
  const reload = () => void client.invalidateQueries({ queryKey: ['easeedesk', 'it-solutions'] })

  const fail = (err: unknown) => toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
  const done = (out: { ok: boolean; message?: string }, said: string) => {
    if (!out.ok) {
      toast(out.message ?? 'Something went wrong.', 'Error', 'error')
      return
    }
    toast(said, 'Success', 'success')
    reload()
  }

  return (
    <Content>
      <h4>
        <strong>Solutions</strong> categories
      </h4>
      <hr />
      {data.canEdit && (
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="col-md-12 aright">
            <button type="button" className="btn btn-default btn-sm btn-primary new-category" onClick={() => setEditing('new')}>
              <i className="fas fa-plus" aria-hidden="true"></i> New category
            </button>
          </div>
        </div>
      )}
      <table className="table table-hover table-striped table-bordered" id="solutions-categories">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Category</th>
            <th>Solutions</th>
            <th>Activate</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.categories.length === 0 && (
            <tr>
              <td colSpan={5}>No solutions categories found.</td>
            </tr>
          )}
          {data.categories.map((c) => (
            <CategoryRows
              key={c.ID}
              category={c}
              open={open === c.ID}
              canEdit={data.canEdit}
              roles={data.roles}
              people={data.people ?? []}
              onToggle={() => setOpen(open === c.ID ? 0 : c.ID)}
              onEdit={() => setEditing(c)}
              onActivate={(on) => activateSolutionsCategory(c.ID, on).then((out) => done(out, on ? 'Category activated.' : 'Category deactivated.')).catch(fail)}
              onRemove={() => {
                if (!window.confirm('Are you sure you want to delete this category?')) return
                removeSolutionsCategory(c.ID).then((out) => done(out, 'Category deleted.')).catch(fail)
              }}
              done={done}
              fail={fail}
            />
          ))}
        </tbody>
      </table>

      {editing !== null && (
        <CategoryModal
          category={editing === 'new' ? null : editing}
          roles={data.roles}
          onClose={() => setEditing(null)}
          onSave={(name, roles) =>
            (editing === 'new' ? createSolutionsCategory(name, roles) : editSolutionsCategory(editing.ID, name, roles))
              .then((out) => {
                done(out, editing === 'new' ? 'Category created.' : 'Category updated.')
                if (out.ok) setEditing(null)
              })
              .catch(fail)
          }
        />
      )}
    </Content>
  )
}

function CategoryRows({ category, open, canEdit, roles, people, onToggle, onEdit, onActivate, onRemove, done, fail }: {
  category: SolutionsAdminCategory
  open: boolean
  canEdit: boolean
  roles: AccessOption[]
  people: AccessOption[]
  onToggle(): void
  onEdit(): void
  onActivate(on: boolean): void
  onRemove(): void
  done(out: { ok: boolean; message?: string }, said: string): void
  fail(err: unknown): void
}) {
  return (
    <>
      <tr className={open ? 'shown' : undefined}>
        <td className="details-control" onClick={onToggle} style={{ cursor: 'pointer' }}>
          <i className={`fas ${open ? 'fa-minus-circle' : 'fa-plus-circle'}`} aria-hidden="true"></i>
        </td>
        <td>{category.category_name}</td>
        <td>{category.solutions_number}</td>
        <td>
          <SettingsCheck className="activate-category" checked={category.status === 1} disabled={!canEdit} onChange={onActivate} aria-label={`Activate ${category.category_name}`} />
        </td>
        <td>
          {canEdit && (
            <>
              <button type="button" className="btn btn-sm btn-default edit-category" onClick={onEdit}>
                <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
              </button>{' '}
              <button type="button" className="btn btn-sm btn-default delete-category" onClick={onRemove}>
                <i className="fas fa-times" aria-hidden="true"></i> Delete
              </button>
            </>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <Subcategories category={category.ID} canEdit={canEdit} roles={roles} people={people} done={done} fail={fail} />
          </td>
        </tr>
      )}
    </>
  )
}

function CategoryModal({ category, roles, onClose, onSave }: { category: SolutionsAdminCategory | null; roles: { id: number; name: string }[]; onClose(): void; onSave(name: string, roles: number[]): void }) {
  const [name, setName] = useState(category?.category_name ?? '')
  const [picked, setPicked] = useState<string[]>((category?.roles ?? []).map(String))

  return (
    <Modal id={category ? 'editCategoryModal' : 'newCategoryModal'} open onClose={onClose} title={
      <>
        <i className="fas fa-pencil-alt" aria-hidden="true"></i> {category ? 'Edit category name' : 'New category'}
      </>
    }>
      <form onSubmit={(ev) => { ev.preventDefault(); onSave(name, picked.map(Number)) }}>
        <div className="form-group">
          <label htmlFor="category-name" className="control-label">
            Category name
          </label>
          <input type="text" className="form-control input-sm" id="category-name" name="category_name" value={name} onChange={(ev) => setName(ev.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="category-roles" className="control-label">
            Who can see it
          </label>
          {/* Nimic bifat = toata lumea, ca in portal. */}
          <SelectPicker id="category-roles" className="form-control" multiple title="Everybody" options={roles.map((r) => ({ value: String(r.id), label: r.name }))} value={picked} onChange={setPicked} />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className={`btn btn-primary ${category ? 'submit-update-category' : 'submit-new-category'}`}>
            {category ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function Subcategories({ category, canEdit, roles, people, done, fail }: {
  category: number
  canEdit: boolean
  roles: AccessOption[]
  people: AccessOption[]
  done(out: { ok: boolean; message?: string }, said: string): void
  fail(err: unknown): void
}) {
  const q = useQuery(solutionsAdminSubcategoriesQuery(category))
  const [editing, setEditing] = useState<'new' | SolutionsAdminSubcategory | null>(null)

  if (!q.data) return <Spinner />

  return (
    <div className="solutions-subcategories">
      <div className="row">
        <div className="col-md-6">
          <h4>Solutions</h4>
        </div>
        <div className="col-md-6 aright">
          {canEdit && (
            <button type="button" className="btn btn-default btn-sm btn-primary new-subcategory" onClick={() => setEditing('new')}>
              <i className="fas fa-plus" aria-hidden="true"></i> New solution
            </button>
          )}
        </div>
      </div>
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Solution</th>
            <th>Level 0</th>
            <th>Level 1</th>
            <th>Level 2</th>
            <th>Level 3</th>
            <th>Priority</th>
            <th>Active</th>
            <th>Access</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {q.data.subcategories.length === 0 && (
            <tr>
              <td colSpan={9}>No solutions yet.</td>
            </tr>
          )}
          {q.data.subcategories.map((s) => (
            <tr key={s.ID}>
              <td>{s.subcategory_name}</td>
              <td>{s.levels['0'] ?? ''}</td>
              <td>{s.levels['1'] ?? ''}</td>
              <td>{s.levels['2'] ?? ''}</td>
              <td>{s.levels['3'] ?? ''}</td>
              <td>{PRIORITIES.find(([n]) => n === s.priority)?.[1] ?? '-'}</td>
              <td>
                <SettingsCheck
                  className="icheck-sub"
                  checked={s.status === 1}
                  disabled={!canEdit}
                  aria-label={`Activate ${s.subcategory_name}`}
                  onChange={(on) => activateSolutionsSubcategory(s.ID, on).then((out) => done(out, on ? 'Solution activated.' : 'Solution deactivated.')).catch(fail)}
                />
              </td>
              <td className="document-access">{accessLabel(s.access)}</td>
              <td className="aright">
                {canEdit && (
                  <>
                    <button type="button" className="btn btn-sm btn-default edit-subcategory" onClick={() => setEditing(s)}>
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-default delete-subcategory"
                      onClick={() => {
                        if (!window.confirm('Are you sure you want to delete this solution?')) return
                        removeSolutionsSubcategory(s.ID).then((out) => done(out, 'Solution deleted.')).catch(fail)
                      }}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i> Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing !== null && (
        <SubcategoryModal
          subcategory={editing === 'new' ? null : editing}
          roles={roles}
          people={people}
          onClose={() => setEditing(null)}
          onSave={(form, access) =>
            (editing === 'new' ? createSolutionsSubcategory(category, form, access) : editSolutionsSubcategory(editing.ID, form, access))
              .then((out) => {
                done(out, editing === 'new' ? 'Solution created.' : 'Solution updated.')
                if (out.ok) setEditing(null)
              })
              .catch(fail)
          }
        />
      )}
    </div>
  )
}

/** Ce scrie in coloana "Access": mosteneste, sau cati sunt bifati. */
function accessLabel(access: DocumentAccess | undefined): string {
  if (!access || (access.roles.length === 0 && access.users.length === 0)) return 'Same as the category'
  const parts: string[] = []
  if (access.roles.length > 0) parts.push(`${access.roles.length} ${access.roles.length === 1 ? 'role' : 'roles'}`)
  if (access.users.length > 0) parts.push(`${access.users.length} ${access.users.length === 1 ? 'person' : 'people'}`)

  return `Restricted: ${parts.join(', ')}`
}

function SubcategoryModal({ subcategory, roles, people, onClose, onSave }: {
  subcategory: SolutionsAdminSubcategory | null
  roles: AccessOption[]
  people: AccessOption[]
  onClose(): void
  onSave(form: SubcategoryForm, access: DocumentAccess): void
}) {
  const [access, setAccess] = useState<DocumentAccess>(subcategory?.access ?? INHERIT_ACCESS)
  const [form, setForm] = useState<SubcategoryForm>(
    subcategory
      ? {
          subcategory_name: subcategory.subcategory_name,
          priority: subcategory.priority,
          level_0: subcategory.levels['0'] ?? '',
          level_1: subcategory.levels['1'] ?? '',
          level_2: subcategory.levels['2'] ?? '',
          level_3: subcategory.levels['3'] ?? '',
        }
      : EMPTY_SUB
  )
  const set = <K extends keyof SubcategoryForm>(key: K, value: SubcategoryForm[K]) => setForm((f) => ({ ...f, [key]: value }))

  return (
    <Modal id={subcategory ? 'editSubcategoryModal' : 'newSubcategoryModal'} open onClose={onClose} title={subcategory ? 'Edit solution' : 'New solution'}>
      <form onSubmit={(ev) => { ev.preventDefault(); onSave(form, access) }}>
        <div className="form-group">
          <label htmlFor="subcategory-name" className="control-label">
            Solution name
          </label>
          <input type="text" className="form-control input-sm" id="subcategory-name" name="subcategory_name" value={form.subcategory_name} onChange={(ev) => set('subcategory_name', ev.target.value)} />
        </div>
        {([0, 1, 2, 3] as const).map((n) => (
          <div className="form-group" key={n}>
            <label htmlFor={`level-${n}`} className="control-label">
              Level {n} description
            </label>
            <input type="text" className="form-control input-sm" id={`level-${n}`} name={`level_${n}`} value={form[`level_${n}`]} onChange={(ev) => set(`level_${n}`, ev.target.value)} />
          </div>
        ))}
        <div className="form-group">
          <label htmlFor="priority" className="control-label">
            Priority
          </label>
          <SelectPicker id="priority" className="form-control" title="Select.." options={PRIORITIES.map(([n, label]) => ({ value: String(n), label }))} value={form.priority > 0 ? String(form.priority) : ''} onChange={(v) => set('priority', Number(v))} />
        </div>
        <DocumentAccessPicker idPrefix="subcategory-access" roles={roles} people={people} value={access} onChange={setAccess} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className={`btn btn-primary ${subcategory ? 'submit-edit-subcategory' : 'submit-new-subcategory'}`}>
            {subcategory ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
